import Link from "next/link";
import { Icon } from "@iconify/react";

export function HowItWorksSection() {
  return (
    <section id="how-it-works" className="py-14 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-6xl">
        <div className="mx-auto max-w-3xl text-center">
          <span className="text-xs font-medium uppercase tracking-widest text-[#7C8D52]">How it works</span>
          <h2 className="mt-4 text-3xl font-normal tracking-tight text-[#2B3624] sm:text-4xl lg:text-5xl">
            From kickoff to clear next steps.
          </h2>
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {steps.map((step) => (
            <article key={step.num} className="rounded-2xl border border-[#2B3624]/10 bg-[#EBE6DA] p-6">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#2B3624] text-sm font-medium text-white">
                {step.num}
              </span>
              <h3 className="mt-8 text-xl font-medium text-[#2B3624]">{step.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#66705F]">{step.body}</p>
              <div className="mt-8 rounded-xl border border-[#2B3624]/10 bg-white p-4 shadow-sm">{step.preview}</div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

const steps = [
  {
    num: "01",
    title: "Create a workspace",
    body: "Start with an existing project or begin with a kickoff meeting.",
    preview: (
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#EFF3DE] text-[#64724A]">
          <Icon icon="solar:add-folder-linear" className="text-xl" />
        </span>
        <div>
          <p className="text-xs font-semibold">New workspace</p>
          <p className="text-xs text-[#8A9185]">Client launch project</p>
        </div>
      </div>
    ),
  },
  {
    num: "02",
    title: "Bring in your team and knowledge",
    body: "Invite members, upload files, paste notes, or start a live session.",
    preview: (
      <div className="flex items-center justify-between">
        <div className="flex -space-x-2">
          {["M", "J", "A"].map((initial, i) => (
            <span
              key={initial}
              className={`flex h-8 w-8 items-center justify-center rounded-full border-2 border-white text-xs text-white ${
                i === 0 ? "bg-[#C8A58B]" : i === 1 ? "bg-[#7C8D9B]" : "bg-[#8B9277]"
              }`}
            >
              {initial}
            </span>
          ))}
        </div>
        <span className="rounded-md border border-[#2B3624]/10 px-3 py-2 text-xs font-medium">Invite team</span>
      </div>
    ),
  },
  {
    num: "03",
    title: "Let the workspace organize itself",
    body: "CollaboratorX captures context, updates workspace intelligence, and helps the team act on what matters.",
    preview: (
      <div>
        <p className="flex items-center gap-2 text-xs font-semibold text-[#56643E]">
          <Icon icon="solar:stars-minimalistic-linear" />
          Intelligence updated
        </p>
        <p className="mt-2 text-xs text-[#66705F]">2 tasks · 1 decision · 1 next step</p>
      </div>
    ),
  },
];

export function LiveSessionsSection() {
  return (
    <section className="border-t border-[#2B3624]/[0.06] py-14 sm:py-20 lg:py-28">
      <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
        <div className="order-2 lg:order-1">
          <div className="rounded-2xl border border-[#2B3624]/10 bg-[#EBE6DA] p-4 shadow-[0_25px_60px_-35px_rgba(43,54,36,0.45)]">
            <div className="overflow-hidden rounded-xl border border-[#2B3624]/10 bg-white">
              <div className="flex items-center justify-between border-b border-[#2B3624]/10 px-4 py-3">
                <div>
                  <p className="text-xs font-semibold">Northstar weekly review</p>
                  <p className="text-xs text-[#8A9185]">Live · 4 participants</p>
                </div>
                <span className="flex items-center gap-1.5 text-xs font-medium text-[#B85C50]">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#B85C50]" />
                  Recording
                </span>
              </div>
              <div className="grid sm:grid-cols-5">
                <div className="bg-[#20291D] p-3 sm:col-span-3">
                  <div className="grid grid-cols-2 gap-2">
                    {["MC", "JB", "AS", "RK"].map((initials, i) => (
                      <div
                        key={initials}
                        className={`flex items-center justify-center rounded-lg text-white ${
                          i < 2 ? "h-28 text-xl" : "h-24 text-lg"
                        } ${["bg-[#65725E]", "bg-[#596A73]", "bg-[#796F62]", "bg-[#505C4C]"][i]}`}
                      >
                        {initials}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-4 sm:col-span-2">
                  <p className="text-xs font-semibold text-[#2B3624]">Live intelligence</p>
                  <div className="mt-4 space-y-3">
                    <div className="rounded-lg bg-[#EFF3DE] p-3">
                      <p className="text-xs font-medium text-[#68774C]">Task generated</p>
                      <p className="mt-1 text-xs text-[#2B3624]">Jon to update rollout plan by Friday.</p>
                    </div>
                    <div className="rounded-lg bg-[#F3F0E9] p-3">
                      <p className="text-xs font-medium text-[#7B8277]">Decision captured</p>
                      <p className="mt-1 text-xs text-[#2B3624]">Pilot launch moves to September.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="order-1 lg:order-2">
          <span className="text-xs font-medium uppercase tracking-widest text-[#7C8D52]">Live sessions</span>
          <h2 className="mt-4 text-3xl font-normal tracking-tight text-[#2B3624] sm:text-4xl lg:text-5xl">
            Meet without losing the work behind the meeting.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#5F675B] sm:text-lg">
            Run chat, video calls, and transcription inside the workspace. CollaboratorX captures key points,
            decisions, tasks, risks, and questions while the conversation is happening.
          </p>
          <Link
            href="/signup"
            className="mt-8 inline-flex items-center gap-2 rounded-md bg-[#2B3624] px-5 py-3 text-sm font-medium text-white"
          >
            Start a live workspace
            <Icon icon="solar:arrow-right-linear" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export function WorkspaceMemorySection() {
  return (
    <section className="border-t border-[#2B3624]/[0.06] py-14 sm:py-20 lg:py-28">
      <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
        <div>
          <span className="text-xs font-medium uppercase tracking-widest text-[#7C8D52]">Workspace memory</span>
          <h2 className="mt-4 text-3xl font-normal tracking-tight text-[#2B3624] sm:text-4xl lg:text-5xl">
            Your workspace gets smarter as the project grows.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#5F675B] sm:text-lg">
            CollaboratorX maintains a living understanding of the workspace. It remembers decisions, project
            context, team preferences, open issues, and how the work is progressing.
          </p>
          <p className="mt-6 flex items-center gap-2 text-sm font-medium text-[#2B3624]">
            <Icon icon="solar:stars-minimalistic-linear" className="text-[#7C8D52]" />
            Your workspace AI already knows the project.
          </p>
        </div>

        <div className="rounded-2xl border border-[#2B3624]/10 bg-[#EBE6DA] p-4 shadow-[0_25px_60px_-35px_rgba(43,54,36,0.45)]">
          <div className="rounded-xl border border-[#2B3624]/10 bg-white p-5">
            <div className="flex items-center gap-3 rounded-lg border border-[#2B3624]/10 bg-[#FAF9F6] px-4 py-3">
              <Icon icon="solar:magnifer-linear" className="text-[#7B8277]" />
              <span className="text-sm text-[#5F675B]">What changed since the last client meeting?</span>
            </div>
            <div className="mt-5 flex gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#2B3624] text-white">
                <Icon icon="solar:stars-minimalistic-linear" />
              </span>
              <div>
                <p className="text-xs font-semibold text-[#2B3624]">Northstar AI</p>
                <p className="mt-2 text-sm leading-relaxed text-[#5F675B]">
                  Three meaningful updates: the pilot moved to September, campaign messaging was approved, and
                  production is blocked until final assets arrive.
                </p>
                <p className="mt-4 text-xs text-[#8A9185]">Grounded in 3 meetings, 18 messages, and 6 files</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function FlowCanvasSection() {
  return (
    <section className="border-t border-[#2B3624]/[0.06] py-14 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-6xl">
        <div className="grid items-end gap-8 lg:grid-cols-2">
          <div>
            <span className="text-xs font-medium uppercase tracking-widest text-[#7C8D52]">Flow canvas</span>
            <h2 className="mt-4 text-3xl font-normal tracking-tight text-[#2B3624] sm:text-4xl lg:text-5xl">
              Turn discussion into visible execution.
            </h2>
          </div>
          <p className="text-base leading-relaxed text-[#5F675B] sm:text-lg">
            Map project stages, dependencies, approvals, blockers, and next actions in a shared flow canvas
            connected to the rest of the workspace.
          </p>
        </div>

        <div className="mt-12 rounded-2xl border border-[#2B3624]/10 bg-[#EBE6DA] p-5 shadow-[0_25px_60px_-35px_rgba(43,54,36,0.45)] lg:overflow-x-auto">
          <div
            className="rounded-xl border border-[#2B3624]/10 bg-[#F8F6F1] p-6 lg:min-w-[55rem]"
            style={{
              backgroundImage: "radial-gradient(rgba(43,54,36,0.12) 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-[#2B3624]">Northstar delivery flow</p>
                <p className="mt-1 text-xs text-[#8A9185]">Connected to tasks, decisions, and workspace knowledge</p>
              </div>
            </div>

            <div className="mt-8 flex flex-col gap-3 lg:hidden">
              {flowStages.map((stage, i) => (
                <div key={stage.name}>
                  <div className={`rounded-xl border p-4 shadow-sm ${stage.className.replace(/\bw-\d+\b/g, "")}`}>
                    <span className={`rounded-full px-2 py-1 text-xs font-medium ${stage.badgeClass}`}>
                      {stage.status}
                    </span>
                    <p className="mt-5 text-sm font-semibold">{stage.name}</p>
                    <p className="mt-2 text-xs text-[#7B8277]">{stage.meta}</p>
                  </div>
                  {i < flowStages.length - 1 && (
                    <div className="flex justify-center py-1">
                      <Icon icon="solar:arrow-down-linear" className="text-xl text-[#8A9185]" />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8 hidden items-center justify-between lg:flex">
              {flowStages.map((stage, i) => (
                <div key={stage.name} className="flex items-center">
                  <div className={`w-36 rounded-xl border p-4 shadow-sm ${stage.className}`}>
                    <span className={`rounded-full px-2 py-1 text-xs font-medium ${stage.badgeClass}`}>
                      {stage.status}
                    </span>
                    <p className="mt-5 text-sm font-semibold">{stage.name}</p>
                    <p className="mt-2 text-xs text-[#7B8277]">{stage.meta}</p>
                  </div>
                  {i < flowStages.length - 1 && (
                    <Icon icon="solar:arrow-right-linear" className="mx-1 text-xl text-[#8A9185]" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const flowStages = [
  {
    name: "Discovery",
    meta: "Maya · Aug 12",
    status: "Complete",
    className: "border-[#9EAE78] bg-[#EFF3DE]",
    badgeClass: "bg-[#7C8D52] text-white",
  },
  {
    name: "Strategy",
    meta: "Jon · Aug 23",
    status: "Active",
    className: "border-2 border-[#7C8D52] bg-white shadow-lg",
    badgeClass: "bg-[#E6EBCB] text-[#5D6C42]",
  },
  {
    name: "Client approval",
    meta: "Assets required",
    status: "Blocked",
    className: "border-[#D8A89F] bg-[#FFF4F1] w-40",
    badgeClass: "bg-[#B85C50] text-white",
  },
  {
    name: "Production",
    meta: "Ari · Sep 04",
    status: "Upcoming",
    className: "border-[#2B3624]/10 bg-white",
    badgeClass: "bg-[#E9E7E1] text-[#7B8277]",
  },
  {
    name: "Launch",
    meta: "Sep 18",
    status: "Upcoming",
    className: "border-[#2B3624]/10 bg-white w-32",
    badgeClass: "bg-[#E9E7E1] text-[#7B8277]",
  },
];

const useCases = [
  { icon: "solar:case-linear", title: "Consulting Firms", body: "Create one intelligent workspace per client." },
  { icon: "solar:palette-linear", title: "Agencies", body: "Keep briefs, calls, approvals, files, and deliverables together." },
  { icon: "solar:programming-linear", title: "Product Teams", body: "Capture decisions, roadmaps, meetings, and execution in one place." },
  {
    icon: "solar:square-academic-cap-linear",
    title: "Training and Service Companies",
    body: "Manage cohorts, clients, sessions, assignments, and follow-ups.",
  },
] as const;

export function SolutionsSection() {
  return (
    <section
      id="solutions"
      className="-mx-5 bg-[#1D2718] px-5 py-14 text-[#F6F3EC] sm:-mx-8 sm:px-8 sm:py-20 lg:-mx-12 lg:px-12 lg:py-28 xl:-mx-16 xl:px-16"
    >
      <div className="mx-auto max-w-6xl">
        <div className="max-w-3xl">
          <span className="text-xs font-medium uppercase tracking-widest text-[#C9D68A]">Use cases</span>
          <h2 className="mt-4 text-3xl font-normal tracking-tight sm:text-4xl lg:text-5xl">
            Built for work that moves across people, projects, and clients.
          </h2>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {useCases.map((item) => (
            <article key={item.title} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
              <Icon icon={item.icon} className="text-2xl text-[#C9D68A]" />
              <h3 className="mt-8 text-lg font-medium">{item.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#D8D0B8]">{item.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

const samplePrompts = [
  "What did we decide about the launch timeline?",
  "Which tasks are still blocked?",
  "Prepare the agenda for our next client call.",
  "Summarize everything the client requested.",
  "What changed since the last meeting?",
];

export function WorkspaceAiSection() {
  return (
    <section className="py-14 sm:py-20 lg:py-28">
      <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
        <div>
          <span className="text-xs font-medium uppercase tracking-widest text-[#7C8D52]">Workspace AI</span>
          <h2 className="mt-4 text-3xl font-normal tracking-tight text-[#2B3624] sm:text-4xl lg:text-5xl">
            Every workspace gets its own AI assistant.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#5F675B] sm:text-lg">
            The assistant understands only the workspace it belongs to. It can answer questions, summarize
            history, follow up on tasks, prepare meetings, retrieve decisions, and help the team move forward.
          </p>
          <div className="mt-8 space-y-2">
            {samplePrompts.map((prompt) => (
              <p
                key={prompt}
                className="rounded-lg border border-[#2B3624]/10 bg-white/50 px-4 py-3 text-sm text-[#5F675B]"
              >
                {prompt}
              </p>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-[#2B3624]/10 bg-[#EBE6DA] p-4 shadow-[0_25px_60px_-35px_rgba(43,54,36,0.45)]">
          <div className="overflow-hidden rounded-xl border border-[#2B3624]/10 bg-white">
            <div className="flex items-center gap-3 border-b border-[#2B3624]/10 px-5 py-4">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#2B3624] text-white">
                <Icon icon="solar:stars-minimalistic-linear" />
              </span>
              <div>
                <p className="text-sm font-semibold">Northstar AI</p>
                <p className="text-xs text-[#7C8D52]">Understands this workspace</p>
              </div>
            </div>
            <div className="space-y-5 p-5">
              <div className="ml-auto max-w-sm rounded-l-xl rounded-tr-xl bg-[#2B3624] px-4 py-3 text-sm leading-relaxed text-white">
                Prepare the agenda for our next client call.
              </div>
              <div className="flex gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#EFF3DE] text-[#64724A]">
                  <Icon icon="solar:stars-minimalistic-linear" />
                </span>
                <div className="max-w-md rounded-r-xl rounded-bl-xl bg-[#F3F0E9] px-4 py-3 text-sm leading-relaxed text-[#4B5563]">
                  I prepared a 30-minute agenda based on the open approval, launch timeline, and tasks from
                  your last session.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
